import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getCompanyBranding, getCompanyEffectiveBranding} from "../services/companyBrandingService";
import {CompanyBrandingResponse} from "../services/companyBrandingService";
import {
    getBrandingCache,
    getBrandingCacheTimestamp,
    setBrandingCache,
    removeBrandingCache,
} from "../utils/brandingCacheUtils";
import {useEffect} from "react";
import {useCompanyFeatureFlag} from "./featureFlagsHook";
import {FeatureFlagEnum} from "../types/featureFlagsEnum";
const BRANDING_CACHE_KEY = "company-branding";
const STALE_TIME = 1000 * 60 * 30; // 30 minutes
const GC_TIME = 1000 * 60 * 60 * 24 * 30; // 30 days

export const useAdminCompanyBrandingQuery = (companyId?: string) => {
    return useQuery<CompanyBrandingResponse>({
        queryKey: ["adminBranding", companyId],
        queryFn: async () => {
            if (!companyId) throw new Error("No company ID");
            return await getCompanyBranding(companyId);
        },
        enabled: !!companyId,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
};

export const useCompanyBrandingQuery = (companyId?: string) => {
    const cachedData = getBrandingCache(companyId);
    const cachedTimestamp = getBrandingCacheTimestamp(companyId);
    const enabled = useCompanyFeatureFlag(FeatureFlagEnum.whiteLabeling);

    // Invalidate the cache if the cached data is not present or if the companyId changes
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!cachedData && companyId) {
            void queryClient.invalidateQueries({queryKey: [BRANDING_CACHE_KEY, companyId]});
        }
    }, [companyId, cachedData, queryClient]);

    return useQuery<string | null>({
        queryKey: [BRANDING_CACHE_KEY, companyId],
        queryFn: async () => {
            if (!companyId) throw new Error("No company ID");
            const branding = await getCompanyEffectiveBranding(companyId);
            if (branding?.isEnabled && branding?.logoUrl) {
                setBrandingCache(companyId, branding.logoUrl, true);
                return branding.logoUrl;
            } else {
                removeBrandingCache(companyId);
                return null;
            }
        },
        initialData: cachedData,
        initialDataUpdatedAt: cachedTimestamp,
        enabled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};
